import type { AccessPrivilege } from '@stopal/shared/access-privilege'
import type { NexusTileId } from '@stopal/shared/nexus-tiles'
import { isSuperadmin } from '@/components/domain'
import type { NexusIconName } from '@/components/nexus/shell/icon'
import { getTab } from './tab-registry'

export interface Tile {
  /**
   * DERIVED from `NEXUS_TILE_IDS` (`@stopal/shared/nexus-tiles`), not a free
   * string: the API's `preferences.update` validates a pinned list against that
   * same array, so a tile added here without an entry there would be a tile
   * nobody can pin. Typing it this way makes that a build failure instead.
   */
  id: NexusTileId
  /** Tab name to open (must match a registered TabDef). */
  tab: string
  /** Lucide icon name (overrides the tab's default icon for the tile). */
  icon?: NexusIconName
  /** Display label override (defaults to the tab's label with empty props). */
  label?: string
  /** Privilege required for this tile (defaults to the tab's permission). */
  gate?: AccessPrivilege
}

export interface Section {
  id: string
  label: string
  /** Privilege required for the entire section to render. */
  gate?: AccessPrivilege
  /**
   * Role required for the section to render. Use for sections that are
   * gated on identity rather than a specific privilege — currently only
   * the Dev section (superadmin-only by design, no privilege grants it).
   */
  role?: string
  tiles: Tile[]
}

export const SECTIONS: Section[] = [
  {
    id: 'logistics',
    label: 'Logistics',
    // No section-level gate — tab gates decide visibility, so warehouse-only
    // staff (access_warehouses, no access_ops) still see Inventory + Assign.
    tiles: [
      {
        id: 'logistics.dashboard',
        tab: 'dashboard',
        label: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        id: 'logistics.dispatch',
        tab: 'dispatch',
        label: 'Dispatch',
        icon: 'Truck',
      },
      {
        id: 'logistics.inventory',
        tab: 'inventory',
        label: 'Inventory',
        icon: 'PackagePlus',
      },
      {
        id: 'logistics.assign',
        tab: 'assign',
        label: 'Assign',
        icon: 'ClipboardList',
      },
      {
        id: 'logistics.trackers',
        tab: 'trackers',
        label: 'Trackers',
        icon: 'Radio',
      },
      {
        id: 'logistics.print-queue',
        tab: 'print-queue',
        label: 'Print queue',
        icon: 'Printer',
      },
      {
        id: 'logistics.scan-and-go',
        tab: 'scan-and-go',
        label: 'Scan & Go',
        icon: 'ScanQrCode',
      },
      {
        id: 'logistics.scan-overrides',
        tab: 'scan-overrides',
        label: 'Unscanned',
        icon: 'ScanBarcode',
      },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    gate: 'access_ops',
    tiles: [
      {
        id: 'ops.dashboard',
        tab: 'dashboard',
        label: 'Dashboard',
        icon: 'LayoutDashboard',
      },
      {
        id: 'ops.order-desk',
        tab: 'order-desk',
        label: 'Order desk',
        icon: 'PhoneCall',
        // The desk's own privilege, not the Customers one it is parented on:
        // this page places orders and charges cards, and a tile that offered it
        // to everyone holding the customer list would be granting that by
        // implication. `orderDeskProcedure` asks for the same name, so the tile
        // cannot offer a desk whose doors refuse.
        gate: 'access_order_desk',
      },
      {
        id: 'ops.orders',
        tab: 'orders',
        label: 'Orders',
        icon: 'ShoppingCart',
      },
      {
        id: 'ops.warehouses',
        tab: 'warehouses',
        label: 'Warehouses',
        icon: 'Warehouse',
      },
      {
        id: 'ops.customers',
        tab: 'customers',
        label: 'Customers',
        icon: 'Users',
      },
      {
        id: 'ops.business-accounts',
        tab: 'business-accounts',
        label: 'Business',
        icon: 'Building2',
        // A business account IS a customer record, so it reuses the Customers
        // privilege rather than earning a 14th. The privilege tree requires the
        // parent (`access_ops`), which is what the ops procedures behind this
        // page actually enforce — so the tile can never render for somebody the
        // API would refuse. The tab owns that leaf gate.
      },
      {
        id: 'ops.products',
        tab: 'products',
        label: 'Products',
        icon: 'Package',
        // Privilege slug predates the Pricing→Products rename; changing
        // it is a DB migration, intentionally out of scope.
      },
      {
        id: 'ops.discounts',
        tab: 'discounts',
        label: 'Discounts',
        icon: 'Percent',
      },
      {
        id: 'ops.billing',
        tab: 'billing',
        label: 'Billing',
        icon: 'CreditCard',
      },
      {
        id: 'ops.messages',
        tab: 'messages',
        label: 'Messages',
        icon: 'MessageSquare',
      },
      {
        id: 'ops.territories',
        tab: 'territories',
        label: 'Territories',
        icon: 'MapPin',
      },
      {
        id: 'ops.quotes',
        tab: 'quotes',
        label: 'Move quotes',
        icon: 'FileText',
      },
      {
        // The tab owns the leaf gate, and the procedures behind it agree:
        // every `carrierShipment.*` door is `carrierMovesProcedure`.
        id: 'ops.carrier-moves',
        tab: 'carrier-moves',
        label: 'Carrier desk',
        icon: 'Truck',
      },
      {
        id: 'ops.marketing',
        tab: 'marketing',
        label: 'Marketing',
        icon: 'Megaphone',
      },
    ],
  },
  {
    id: 'business-development',
    label: 'Business development',
    // NOT inside Operations. That section gates on `access_ops`, so a referrals
    // tile placed there would be hidden from exactly the BD operator it is for
    // — `access_referrals` is a ROOT privilege and its holder has no Operations
    // floor.
    //
    // The gate is REDUNDANT today and deliberately kept: the section holds one
    // tile whose tab declares the same privilege, so deleting this line changes
    // no persona's launcher and `sections.test.ts` stays green — measured, not
    // assumed. It is the floor for a SECOND tile whose own tab declares a
    // weaker permission: `canListTab` would list that one for anyone clearing
    // the weaker gate, and only `hasSectionAccess` would refuse them. (A tile
    // carrying a mismatched gate of its own is a different case, and already
    // fails `keeps every tile's effective privilege aligned with its tab`.)
    gate: 'access_referrals',
    tiles: [
      {
        // Deliberately no tile gate: the tab owns `access_referrals` and a tile
        // gate SHADOWS a tab permission, so a second spelling here could only
        // ever drift away from the one the page's `referralsProcedure`
        // enforces.
        id: 'bd.referrals',
        tab: 'referrals',
        label: 'Referrals',
        icon: 'Handshake',
      },
    ],
  },
  {
    id: 'maestro',
    label: 'Maestro',
    gate: 'access_maestro',
    tiles: [
      {
        id: 'maestro.tasks',
        tab: 'tasks',
        label: 'Tasks',
        icon: 'ListChecks',
      },
      {
        id: 'maestro.documents',
        tab: 'documents',
        label: 'Documents',
        icon: 'FileText',
      },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    // Maestro Settings lives here (admin-tier), not Dev.
    gate: 'access_admin',
    tiles: [
      {
        // The tab owns `access_users`, whose parent is this section's
        // `access_admin`. The procedures split reads from writes: `user.get`
        // and `user.listPrivileges` are `usersProcedure`, `user.list` is
        // shared with the business-account owner picker so it takes either
        // `access_users` or `access_customers`, and every write stays
        // `adminProcedure`.
        id: 'admin.users',
        tab: 'users',
        label: 'Users',
        icon: 'Users',
      },
      {
        // Deliberately no tile gate — the `admin.notifications` shape below.
        // Every WRITE on this page is `adminProcedure` (the superadmin ROLE),
        // so the tab def carries `role: 'superadmin'`, which `canListTab`
        // checks before a tile gate and which a tile gate cannot shadow.
        //
        // `organization.list` is `adminProcedure`, while
        // `project.listWithCounts` remains `maestroProcedure`. The tab stays
        // role-gated because the page's writes are admin-only and its mixed
        // reads still do not describe an `access_maestro_settings` capability.
        //
        // `access_maestro_settings` remains seeded and offered by the
        // privilege picker, but no section, tab or procedure reads it.
        // `access_users` is not in that orphaned set: the Users tab
        // declares it, and `user.get` / `user.listPrivileges` enforce it.
        id: 'admin.maestro-settings',
        tab: 'maestro-settings',
        label: 'Maestro Settings',
        icon: 'Sliders',
      },
      {
        id: 'admin.taxonomy',
        tab: 'taxonomy',
        label: 'Taxonomy',
        icon: 'Tags',
      },
      {
        id: 'admin.notifications',
        tab: 'notifications',
        label: 'Notifications',
        icon: 'Bell',
        // Deliberately no tile gate. The tab def carries `role: 'superadmin'`,
        // which `canListTab` checks BEFORE a tile gate and which a tile gate
        // cannot shadow — so this tile is refused for everyone else, and the
        // page's `adminProcedure` refuses them again. Adding a privilege here
        // would not widen it and would imply a grant that does not exist.
      },
    ],
  },
  {
    id: 'dev',
    label: 'Dev',
    // Dev stays superadmin-only by design — no privilege grants it. Projects
    // and Organizations live here because their management is platform-level.
    role: 'superadmin',
    tiles: [
      {
        id: 'dev.workflows',
        tab: 'workflows',
        label: 'Workflows',
        icon: 'Workflow',
      },
      { id: 'dev.configs', tab: 'configs', label: 'Configs', icon: 'Settings' },
      { id: 'dev.legal', tab: 'legal', label: 'Legal', icon: 'Scale' },
      {
        id: 'dev.backups',
        tab: 'backups',
        label: 'Backups',
        icon: 'HardDrive',
      },
      {
        id: 'dev.projects',
        tab: 'projects',
        label: 'Projects',
        icon: 'FolderKanban',
      },
      {
        id: 'dev.design-system',
        tab: 'design-system',
        label: 'Design System',
        icon: 'Palette',
      },
    ],
  },
]

/**
 * Superadmin bypasses every gate on the frontend — the launcher / search /
 * shell are about *visibility*, and visibility for superadmin is always on.
 * Server-side write protection (Action privileges like `manage_*`) still
 * enforces explicit grants in `requirePrivilege` — this helper only decides
 * what tiles render.
 */
export function hasGate(
  gate: string | undefined,
  privileges: string[],
  role?: string,
): boolean {
  if (!gate) return true
  if (isSuperadmin(role)) return true
  return privileges.includes(gate)
}

/**
 * The sections and tiles a persona is LISTED, with both gates applied.
 *
 * Exported so `useLauncherTiles` and its tests run the same composition. A test
 * that re-implements this cannot see the hook losing a filter: deleting the
 * hook's own `hasSectionAccess` call left the whole web suite green, because
 * the only thing checking that behaviour was a second copy of it.
 *
 * Order matters and mirrors the hook: tiles are filtered first, then a section
 * is dropped if its own gates refuse it OR nothing survived inside it.
 */
export function visibleSectionTiles(
  privileges: string[],
  role?: string,
): Array<{ section: Section; tiles: Tile[] }> {
  return SECTIONS.map((section) => ({
    section,
    tiles: section.tiles.filter((tile) =>
      canListTab(getTab(tile.tab), tile.gate, privileges, role),
    ),
  })).filter(
    ({ section, tiles }) =>
      tiles.length > 0 && hasSectionAccess(section, privileges, role),
  )
}

/**
 * Tab-level access check, mirroring {@link hasSectionAccess}: a tab is listed
 * when its `role` requirement is satisfied (if any) AND its `gate` privilege is
 * satisfied (if any). Superadmin bypasses both.
 *
 * **Takes the whole def on purpose.** A tab's gate lives in two fields, and
 * `hasGate(def?.permission, …)` reads only one of them. A tab that moved its
 * gate to `role` then has `permission === undefined`, and `hasGate(undefined, …)`
 * returns TRUE for everyone: a call site written from that pattern fails OPEN,
 * silently. Passing the def makes that unexpressible.
 *
 * An UNRESOLVED def (a tile naming a tab nobody registered) is not listed —
 * it would render a tile that mounts "Unknown tab type", and defaulting an
 * unknown to ungated is the wrong direction for a gate. That check sits ABOVE
 * the superadmin bypass deliberately, so "superadmin bypasses both" means both
 * GATES, not the resolution failure: an empty registry shows a superadmin an
 * empty launcher rather than every tile with fallback labels. Fail-closed is
 * the right default for a gate, and `sections.test.ts` asserts separately that
 * every tile resolves, so the branch is a backstop rather than a live path.
 *
 * This is both the launcher/search listing rule and the `TabSlot` confinement
 * check. Neither use is authorization: both are chrome, and the procedure
 * behind the page remains the real guard.
 */
export function canListTab(
  def: { permission?: string; role?: string } | undefined,
  tileGate: string | undefined,
  privileges: string[],
  role?: string,
): boolean {
  if (!def) return false
  if (isSuperadmin(role)) return true
  if (def.role && def.role !== role) return false
  return hasGate(tileGate ?? def.permission, privileges, role)
}

/**
 * Section-level access check. A section renders when:
 *  - its `role` requirement is satisfied (if any), AND
 *  - its `gate` privilege is satisfied (if any)
 * Superadmin bypasses both.
 */
function hasSectionAccess(
  section: Section,
  privileges: string[],
  role?: string,
): boolean {
  if (isSuperadmin(role)) return true
  if (section.role && section.role !== role) return false
  return hasGate(section.gate, privileges, role)
}
